import { RefreshCw, Clock, Pause, Play, RotateCcw } from 'lucide-react';
import { usePriceRefresh } from '../hooks/usePriceRefresh';
import { PriceStatusIndicator } from './PriceStatusIndicator';

interface PriceRefreshTimerProps {
  className?: string;
  showControls?: boolean;
  compact?: boolean;
}

export function PriceRefreshTimer({
  className = '',
  showControls = true,
  compact = false
}: PriceRefreshTimerProps) {
  const { state, controls, formatTimeRemaining } = usePriceRefresh();

  const getTimerColor = () => {
    if (state.isRefreshing) return 'text-blue-600 dark:text-blue-400';
    if (state.error) return 'text-red-600 dark:text-red-400';
    if (state.timeUntilRefresh <= 60) return 'text-orange-600 dark:text-orange-400'; // Last minute warning
    return 'text-gray-600 dark:text-gray-400';
  };

  const getProgressPercentage = () => {
    const refreshInterval = parseInt(import.meta.env.VITE_PRICE_REFRESH_INTERVAL) || (1 * 60 * 1000);
    const totalSeconds = Math.floor(refreshInterval / 1000);
    return ((totalSeconds - state.timeUntilRefresh) / totalSeconds) * 100;
  };

  if (compact) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <div className="relative">
          <Clock className={`w-4 h-4 ${getTimerColor()}`} />
          {state.isRefreshing && (
            <RefreshCw className="w-3 h-3 text-blue-500 absolute -top-1 -right-1 animate-spin" />
          )}
        </div>
        <span className={`text-sm font-mono ${getTimerColor()}`}>
          {formatTimeRemaining(state.timeUntilRefresh)}
        </span>
        {showControls && (
          <button
            onClick={controls.manualRefresh}
            disabled={state.isRefreshing}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh prices now"
          >
            <RefreshCw className={`w-3 h-3 ${state.isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <Clock className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">
            Price Refresh Timer
          </h3>
        </div>
        <div className="flex items-center space-x-2">
          <PriceStatusIndicator />
          {state.isRefreshing && (
            <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
          <span>Next refresh in:</span>
          <span>{state.refreshCount} refreshes completed</span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-1000 ${state.isRefreshing
                ? 'bg-blue-500 animate-pulse'
                : state.timeUntilRefresh <= 60
                  ? 'bg-orange-500'
                  : 'bg-green-500'
              }`}
            style={{ width: `${getProgressPercentage()}%` }}
          />
        </div>
      </div>

      {/* Countdown Display */}
      <div className="text-center mb-4">
        <div className={`text-2xl font-mono font-bold ${getTimerColor()}`}>
          {formatTimeRemaining(state.timeUntilRefresh)}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {state.isRefreshing ? (
            'Refreshing prices...'
          ) : state.error ? (
            `Error: ${state.error}`
          ) : controls.isPaused ? (
            'Timer paused'
          ) : (
            'Until next price update'
          )}
        </div>
      </div>

      {/* Last Refresh Info */}
      {state.lastRefreshTime && (
        <div className="text-xs text-gray-500 dark:text-gray-400 text-center mb-3">
          Last refresh: {new Date(state.lastRefreshTime).toLocaleTimeString()}
        </div>
      )}

      {/* Controls */}
      {showControls && (
        <div className="flex justify-center space-x-2">
          <button
            onClick={controls.manualRefresh}
            disabled={state.isRefreshing}
            className="flex items-center space-x-1 px-3 py-1.5 text-xs bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-md hover:bg-blue-200 dark:hover:bg-blue-900/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Refresh prices immediately"
          >
            <RefreshCw className={`w-3 h-3 ${state.isRefreshing ? 'animate-spin' : ''}`} />
            <span>Refresh Now</span>
          </button>

          <button
            onClick={controls.isPaused ? controls.resumeTimer : controls.pauseTimer}
            className="flex items-center space-x-1 px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            title={controls.isPaused ? 'Resume timer' : 'Pause timer'}
          >
            {controls.isPaused ? (
              <Play className="w-3 h-3" />
            ) : (
              <Pause className="w-3 h-3" />
            )}
            <span>{controls.isPaused ? 'Resume' : 'Pause'}</span>
          </button>

          <button
            onClick={controls.resetTimer}
            className="flex items-center space-x-1 px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            title="Reset timer to 1 minute"
          >
            <RotateCcw className="w-3 h-3" />
            <span>Reset</span>
          </button>
        </div>
      )}

      {/* Status Indicator */}
      <div className="flex items-center justify-center mt-3 space-x-2">
        <div className={`w-2 h-2 rounded-full ${state.isRefreshing
            ? 'bg-blue-500 animate-pulse'
            : state.error
              ? 'bg-red-500'
              : controls.isPaused
                ? 'bg-yellow-500'
                : 'bg-green-500'
          }`} />
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {state.isRefreshing
            ? 'Refreshing'
            : state.error
              ? 'Error'
              : controls.isPaused
                ? 'Paused'
                : 'Active'
          }
        </span>
      </div>
    </div>
  );
}
